import React, {useEffect, useRef, useState} from "react";
import {useDropzone} from "react-dropzone";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import {read, utils} from "xlsx";
import {
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import api from "../../services/api";
import {useHistory} from "react-router-dom";
import toastError from "../../errors/toastError";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {toast} from "react-toastify";
import { i18n } from '../../translate/i18n';

function WorksheetToDatagrid(ws) {
    /* create an array of arrays - mantendo formatação de células */
    const rawRows = utils.sheet_to_json(ws, {header: 1, defval: "", raw: false});
    if (!rawRows || rawRows.length === 0) {
        return { rows: [], columns: [] };
    }

    // Determinar la cantidad máxima de columnas reales
    let maxCols = 0;
    rawRows.forEach((r) => {
        if (Array.isArray(r) && r.length > maxCols) {
            maxCols = r.length;
        }
    });

    const headerRow = rawRows[0] || [];

    // Filtrar columnas vacías: descartar aquellas sin cabecera y sin ningún dato en filas
    const activeColIndices = [];
    for (let c = 0; c < maxCols; c++) {
        const headerVal = headerRow[c] !== undefined && headerRow[c] !== null ? String(headerRow[c]).trim() : "";
        let hasData = false;
        for (let r = 1; r < rawRows.length; r++) {
            const cellVal = rawRows[r]?.[c] !== undefined && rawRows[r]?.[c] !== null ? String(rawRows[r][c]).trim() : "";
            if (cellVal !== "") {
                hasData = true;
                break;
            }
        }
        if (headerVal !== "" || hasData) {
            activeColIndices.push(c);
        }
    }

    // Mapear filas conservando únicamente las columnas activas
    const rows = rawRows.map((r) =>
        activeColIndices.map((c) => (r[c] !== undefined && r[c] !== null ? r[c] : ""))
    );

    // Definición de columnas con el nombre real de cabecera del archivo
    const columns = activeColIndices.map((origIdx, newIdx) => {
        const rawHeader = headerRow[origIdx] !== undefined && headerRow[origIdx] !== null ? String(headerRow[origIdx]).trim() : "";
        return {
            key: String(newIdx),
            originalIndex: origIdx,
            headerTitle: rawHeader || `Columna ${newIdx + 1}`,
        };
    });

    return {rows, columns};
}


const useStyles = makeStyles((theme) => ({
    xlsTable: {
        width: "100%",
    },
    tableContainer: {
        flex: 1,
        padding: 1,
        overflowX: "auto",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    actions: {
        padding: "12px",
        border: "1px solid #CCC",
        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
        marginTop: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    importOptions: {
        padding: "8px 12px",
        border: "1px solid #CCC",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        marginTop: 6,
        marginBottom: 8,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    error: {
        color: "red",
        marginTop: 1,
        marginLeft: 10,
    },
    buttonImport: {
        marginRight: 12,
    },
    select: {
        minWidth: 160,
    },
    btnImport: {
        backgroundColor: "#ef4444",
        color: "#ffffff",
        fontWeight: 600,
        textTransform: "none",
        borderRadius: 6,
        padding: "6px 18px",
        "&:hover": {
            backgroundColor: "#dc2626",
        },
        "&:active": {
            backgroundColor: "#b91c1c",
        },
        "&.Mui-disabled": {
            backgroundColor: "rgba(239, 68, 68, 0.5)",
            color: "rgba(255, 255, 255, 0.8)",
        }
    },
    btnCancel: {
        backgroundColor: "#10b981",
        color: "#ffffff",
        textTransform: "none",
        borderRadius: 6,
        padding: "6px 16px",
        "&:hover": {
            backgroundColor: "#059669",
        },
        "&:active": {
            backgroundColor: "#047857",
        },
        "&.Mui-disabled": {
            backgroundColor: "rgba(16, 185, 129, 0.5)",
            color: "rgba(255, 255, 255, 0.8)",
        }
    },
    backButtonContainer: {
        textAlign: "center",
        marginTop: 20,
    },
    dropzonePaper: {
        maxWidth: 620,
        margin: "35px auto 20px",
        borderRadius: 16,
        border: "2px dashed rgba(0, 168, 255, 0.45)",
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fbfcfd',
        padding: "40px 30px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.25s ease-in-out",
        boxShadow: theme.palette.type === 'dark' ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.05)",
        "&:hover": {
            borderColor: "#00a8ff",
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 168, 255, 0.04)' : '#f0f8ff',
            boxShadow: "0 6px 24px rgba(0, 168, 255, 0.15)",
            transform: "translateY(-2px)",
        },
    },
    dropzoneIconBox: {
        width: 76,
        height: 76,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 168, 255, 0.1)",
        border: "1px solid rgba(0, 168, 255, 0.25)",
        marginBottom: 16,
        transition: "transform 0.3s ease",
        "& svg": {
            fontSize: 42,
            color: "#00a8ff",
        },
    },
    dropzoneTitle: {
        fontWeight: 600,
        fontSize: "1.15rem",
        color: theme.palette.text.primary,
        textAlign: "center",
        marginBottom: 6,
    },
    dropzoneSubtitle: {
        fontSize: "0.88rem",
        color: theme.palette.text.secondary,
        textAlign: "center",
        marginBottom: 18,
    },
    formatsWrapper: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
        marginBottom: 20,
    },
    formatChip: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 168, 255, 0.08)' : 'rgba(0, 168, 255, 0.06)',
        color: "#00a8ff",
        border: "1px solid rgba(0, 168, 255, 0.25)",
        fontWeight: 600,
        fontSize: "0.75rem",
        height: 24,
    },
    requirementsAlert: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 179, 0, 0.08)' : 'rgba(255, 179, 0, 0.12)',
        border: "1px solid rgba(255, 179, 0, 0.3)",
        borderRadius: 8,
        padding: "10px 14px",
        maxWidth: 540,
        width: "100%",
        boxSizing: "border-box",
    },
    requirementsIcon: {
        color: "#ffb300",
        fontSize: 20,
        flexShrink: 0,
    },
    requirementsText: {
        color: theme.palette.type === 'dark' ? '#ffca28' : '#e65100',
        fontSize: "0.82rem",
        fontWeight: 500,
        lineHeight: 1.4,
    },
    backButton: {
        borderRadius: 8,
        padding: "8px 24px",
        borderColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
        color: theme.palette.text.primary,
        textTransform: "none",
        fontWeight: 500,
        "&:hover": {
            borderColor: theme.palette.text.primary,
            backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
        },
    },
}));

const ContactImport = () => {
    const size = useWindowDimensions();
    const theme = useTheme();

    const [rows, setRows] = useState(null);
    const [columns, setColumns] = useState(null);
    const classes = useStyles();
    const history = useHistory();
    const [contactFieldsAvailable, setContactFieldsAvailable] = useState([]);
    const [columnValue, setColumnValue] = useState({});
    const [selectedFields, setSelectedFields] = useState({}); // Para rastrear selecciones únicas

    const [openingFile, setOpeningFile] = useState(false);
    const [selection, setSelection] = useState({});
    const [invalidFile, setInvalidFile] = useState(false);
    const [error, setError] = useState(null);
    const [countCreated, setCountCreated] = useState(0);
    const [countIgnored, setCountIgnored] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [imported, setImported] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [validateContact, setValidateContact] = useState(true);
    const [importErrors, setImportErrors] = useState([]);

    // Función para converter número serial do Excel para data JavaScript
    const excelSerialToDate = (serialNumber) => {
        if (!serialNumber || serialNumber < 0) return null;
        const daysDifference = 25569;
        const timestamp = (serialNumber - daysDifference) * 86400000;
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
            console.warn('Invalid Excel serial:', serialNumber);
            return null;
        }
        return date;
    };

    // Función helper para detectar y corregir formato de data MM/DD vs DD/MM
    const parseAndFixDateFormat = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return null;
        if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const first = parseInt(parts[0]);
                const second = parseInt(parts[1]);
                const year = parts[2];
                return new Date(parseInt(year), first - 1, second);
            }
        }
        return new Date(dateStr);
    };

    const contactFields = [
        {id: "marca", label: i18n.t("contactImportWpModal.import.fields.marca") || "Marca", required: true},
        {id: "conexion", label: i18n.t("contactImportWpModal.import.fields.conexion") || "Conexión", required: true},
        {id: "queue", label: i18n.t("contactImportWpModal.import.fields.queue") || "Fila / Departamento", required: true},
        {id: "agente", label: i18n.t("contactImportWpModal.import.fields.agente") || "Agente", required: true},
        {id: "name", label: i18n.t("contactImportWpModal.import.fields.name"), required: true},
        {id: "number", label: i18n.t("contactImportWpModal.import.fields.number"), required: true},
        {id: "kanbanBoard", label: i18n.t("contactImportWpModal.import.fields.kanbanBoard") || "Tablero (Kanban)", required: false},
        {id: "kanbanLane", label: i18n.t("contactImportWpModal.import.fields.kanbanLane"), required: false},
        {id: "tags", label: i18n.t("contactImportWpModal.import.fields.tags"), required: false},
        {id: "email", label: i18n.t("contactImportWpModal.import.fields.email"), required: false},
        {id: "birthDate", label: i18n.t("contactImportWpModal.import.fields.birthDate"), required: false},
        {id: "carteira", label: i18n.t("contactImportWpModal.import.fields.wallet"), required: false},
    ];

    const [dbCatalogs, setDbCatalogs] = useState({
        brands: [],
        whatsapps: [],
        queues: [],
        users: [],
        kanbanBoards: [],
        kanbanLanes: [],
        tags: [],
        loaded: false
    });

    useEffect(() => {
        setContactFieldsAvailable(contactFields);

        let isMounted = true;
        const fetchCatalogs = async () => {
            try {
                const [
                    brandsRes,
                    whatsappsRes,
                    queuesRes,
                    usersRes,
                    boardsRes,
                    lanesRes,
                    tagsRes
                ] = await Promise.all([
                    api.get("/brands").catch(() => ({ data: [] })),
                    api.get("/whatsapp").catch(() => ({ data: [] })),
                    api.get("/queue").catch(() => ({ data: [] })),
                    api.get("/users/list").catch(() => ({ data: [] })),
                    api.get("/kanban-boards").catch(() => ({ data: [] })),
                    api.get("/tags/list?kanban=1").catch(() => ({ data: [] })),
                    api.get("/tags/list?kanban=0").catch(() => ({ data: [] }))
                ]);

                if (isMounted) {
                    setDbCatalogs({
                        brands: Array.isArray(brandsRes.data) ? brandsRes.data : [],
                        whatsapps: Array.isArray(whatsappsRes.data) ? whatsappsRes.data : [],
                        queues: Array.isArray(queuesRes.data) ? queuesRes.data : [],
                        users: Array.isArray(usersRes.data) ? usersRes.data : [],
                        kanbanBoards: Array.isArray(boardsRes.data) ? boardsRes.data : [],
                        kanbanLanes: Array.isArray(lanesRes.data) ? lanesRes.data : [],
                        tags: Array.isArray(tagsRes.data) ? tagsRes.data : [],
                        loaded: true
                    });
                }
            } catch (err) {
                console.error("Error al cargar catálogos para validación:", err);
                if (isMounted) {
                    setDbCatalogs(prev => ({ ...prev, loaded: true }));
                }
            }
        };

        fetchCatalogs();

        return () => {
            isMounted = false;
        };
    }, []);

    const validateCell = (row, columnIndex) => {
        const colKey = String(columnIndex);
        const fieldId = columnValue[colKey] || columnValue[columnIndex];
        if (!fieldId) return { valid: true };

        const rawVal = row[columnIndex];
        const cellValue = rawVal !== undefined && rawVal !== null ? String(rawVal).trim() : "";
        const fieldObj = contactFields.find(f => f.id === fieldId);
        const isRequired = fieldObj?.required;

        // 1. Verificación de campo obligatorio vacío
        if (isRequired && cellValue === "") {
            return {
                valid: false,
                errorType: "empty",
                fieldName: fieldObj.label,
                message: `${fieldObj.label} vacío`
            };
        }

        // Si el campo opcional está vacío, es válido
        if (!isRequired && cellValue === "") {
            return { valid: true };
        }

        // 2. Verificación contra base de datos (si los catálogos ya cargaron)
        if (!dbCatalogs.loaded) {
            return { valid: true };
        }

        // MARCA
        if (fieldId === "marca") {
            const found = dbCatalogs.brands.some(
                b => b.name && b.name.trim().toLowerCase() === cellValue.toLowerCase()
            );
            if (!found) {
                return {
                    valid: false,
                    errorType: "not_found",
                    fieldName: fieldObj.label,
                    message: i18n.t("contactImportWpModal.import.cellInvalidBrand") || "Marca no existe"
                };
            }
        }

        // CONEXIÓN
        if (fieldId === "conexion") {
            const cleanNumber = cellValue.replace(/\D/g, "");
            const matchedConn = dbCatalogs.whatsapps.find(w => {
                const matchName = w.name && w.name.trim().toLowerCase() === cellValue.toLowerCase();
                const matchNum = cleanNumber && w.number && String(w.number).replace(/\D/g, "") === cleanNumber;
                return matchName || matchNum;
            });

            if (!matchedConn) {
                return {
                    valid: false,
                    errorType: "not_found",
                    fieldName: fieldObj.label,
                    message: i18n.t("contactImportWpModal.import.cellInvalidConnection") || "Conexión no existe"
                };
            }

            // Validar si pertenece a la Marca seleccionada en esta misma fila
            const marcaColKey = selection["marca"];
            if (marcaColKey !== undefined && marcaColKey !== null) {
                const marcaIdx = Number(marcaColKey);
                const rowMarcaVal = row[marcaIdx] !== undefined && row[marcaIdx] !== null ? String(row[marcaIdx]).trim() : "";
                if (rowMarcaVal) {
                    const matchedBrand = dbCatalogs.brands.find(
                        b => b.name && b.name.trim().toLowerCase() === rowMarcaVal.toLowerCase()
                    );
                    if (matchedBrand && matchedConn.brandId && matchedConn.brandId !== matchedBrand.id) {
                        return {
                            valid: false,
                            errorType: "not_linked",
                            fieldName: fieldObj.label,
                            message: i18n.t("contactImportWpModal.import.cellConnectionNotInBrand") || "Conexión no pertenece a la marca"
                        };
                    }
                }
            }
        }

        // FILA / DEPARTAMENTO
        if (fieldId === "queue") {
            const found = dbCatalogs.queues.some(
                q => q.name && q.name.trim().toLowerCase() === cellValue.toLowerCase()
            );
            if (!found) {
                return {
                    valid: false,
                    errorType: "not_found",
                    fieldName: fieldObj.label,
                    message: i18n.t("contactImportWpModal.import.cellInvalidQueue") || "Fila no existe"
                };
            }
        }

        // AGENTE
        if (fieldId === "agente") {
            const found = dbCatalogs.users.some(u => {
                const matchName = u.name && u.name.trim().toLowerCase() === cellValue.toLowerCase();
                const matchEmail = u.email && u.email.trim().toLowerCase() === cellValue.toLowerCase();
                return matchName || matchEmail;
            });
            if (!found) {
                return {
                    valid: false,
                    errorType: "not_found",
                    fieldName: fieldObj.label,
                    message: i18n.t("contactImportWpModal.import.cellInvalidAgent") || "Agente no existe"
                };
            }
        }

        // TABLERO KANBAN (Opcional)
        if (fieldId === "kanbanBoard") {
            const found = dbCatalogs.kanbanBoards.some(
                kb => kb.name && kb.name.trim().toLowerCase() === cellValue.toLowerCase()
            );
            if (!found) {
                return {
                    valid: false,
                    errorType: "not_found",
                    fieldName: fieldObj.label,
                    message: i18n.t("contactImportWpModal.import.cellInvalidBoard") || "Tablero no existe"
                };
            }
        }

        // ETAPA KANBAN (Opcional)
        if (fieldId === "kanbanLane") {
            const matchedLane = dbCatalogs.kanbanLanes.find(
                l => l.name && l.name.trim().toLowerCase() === cellValue.toLowerCase()
            );
            if (!matchedLane) {
                return {
                    valid: false,
                    errorType: "not_found",
                    fieldName: fieldObj.label,
                    message: i18n.t("contactImportWpModal.import.cellInvalidLane") || "Etapa no existe"
                };
            }

            // Validar si pertenece al Tablero seleccionado en esta misma fila
            const boardColKey = selection["kanbanBoard"];
            if (boardColKey !== undefined && boardColKey !== null) {
                const boardIdx = Number(boardColKey);
                const rowBoardVal = row[boardIdx] !== undefined && row[boardIdx] !== null ? String(row[boardIdx]).trim() : "";
                if (rowBoardVal) {
                    const matchedBoard = dbCatalogs.kanbanBoards.find(
                        b => b.name && b.name.trim().toLowerCase() === rowBoardVal.toLowerCase()
                    );
                    if (matchedBoard && matchedLane.kanbanBoardId && matchedLane.kanbanBoardId !== matchedBoard.id) {
                        return {
                            valid: false,
                            errorType: "not_linked",
                            fieldName: fieldObj.label,
                            message: i18n.t("contactImportWpModal.import.cellLaneNotInBoard") || "Etapa no pertenece al tablero"
                        };
                    }
                }
            }
        }

        // ETIQUETAS (Opcional, separadas por coma)
        if (fieldId === "tags") {
            const tagList = cellValue.split(",").map(t => t.trim()).filter(Boolean);
            for (const tagStr of tagList) {
                const found = dbCatalogs.tags.some(
                    t => t.name && t.name.trim().toLowerCase() === tagStr.toLowerCase()
                );
                if (!found) {
                    return {
                        valid: false,
                        errorType: "not_found",
                        fieldName: fieldObj.label,
                        message: `${i18n.t("contactImportWpModal.import.cellInvalidTags") || "Etiqueta no existe"}: '${tagStr}'`
                    };
                }
            }
        }

        return { valid: true };
    };

    const processImport = async () => {
        setUploading(true);
        setCountCreated(0);
        setCountIgnored(0);
        setImportErrors([]);

        if (!selectedRows || Object.keys(selectedRows).filter(k => selectedRows[k]).length === 0) {
            toastError(i18n.t("contactImportWpModal.import.noContactSelected"));
            setUploading(false);
            return;
        }

        // Validar que todas las columnas obligatorias estén mapeadas
        const missingRequiredColumns = contactFields
            .filter(f => f.required && !selection[f.id])
            .map(f => f.label);

        if (missingRequiredColumns.length > 0) {
            toastError(
                i18n.t("contactImportWpModal.import.missingRequiredColumns", {
                    fields: missingRequiredColumns.join(", ")
                }) || `Debe seleccionar las columnas obligatorias: ${missingRequiredColumns.join(", ")}`
            );
            setUploading(false);
            return;
        }

        // Pre-validar si existen errores de datos en filas seleccionadas
        if (rows && rows.length > 1 && columns && columns.length > 0) {
            let hasPreErrors = false;
            for (let r = 1; r < rows.length; r++) {
                if (selectedRows[r]) {
                    const rowItem = rows[r];
                    for (let c = 0; c < columns.length; c++) {
                        const val = validateCell(rowItem, c);
                        if (!val.valid) {
                            hasPreErrors = true;
                            break;
                        }
                    }
                    if (hasPreErrors) break;
                }
            }
            if (hasPreErrors) {
                toastError(i18n.t("contactImportWpModal.import.fixErrorsToImport") || "Corrija los datos marcados en rojo antes de importar.");
                setUploading(false);
                return;
            }
        }

        if (rows?.length > 1) {
            let importedCount = 0;
            let ignoredCount = 0;
            const errorsList = [];

            for (let index = 1; index < rows.length; index++) {
                if (selectedRows[index]) {
                    const item = rows[index];
                    const contactData = {};

                    for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                        const column = columns[columnIndex];
                        const selectedField = columnValue[column.key];

                        if (selectedField) {
                            if (selectedField === 'number' && item[columnIndex]) {
                                contactData[selectedField] = String(item[columnIndex]).trim();
                            } else if (selectedField === 'birthDate' && item[columnIndex] !== null && item[columnIndex] !== undefined && item[columnIndex] !== '') {
                                let dateValue = item[columnIndex];
                                let parsedDate = null;
                                try {
                                    if (typeof dateValue === 'number' && dateValue > 1 && dateValue < 100000) {
                                        parsedDate = excelSerialToDate(dateValue);
                                    } else if (typeof dateValue === 'string') {
                                        parsedDate = parseAndFixDateFormat(dateValue);
                                    }
                                    if (parsedDate && !isNaN(parsedDate.getTime()) && parsedDate <= new Date()) {
                                        contactData[selectedField] = parsedDate.toISOString().split('T')[0];
                                    }
                                } catch (error) {
                                    console.warn('Error al procesar fecha de nacimiento:', dateValue, error);
                                }
                            } else {
                                contactData[selectedField] = item[columnIndex];
                            }
                        }
                    }

                    // Validar si esta fila específica tiene campos obligatorios vacíos
                    const missingInRow = contactFields
                        .filter(f => f.required && (!contactData[f.id] || String(contactData[f.id]).trim() === ''))
                        .map(f => f.label);

                    if (missingInRow.length > 0) {
                        ignoredCount++;
                        errorsList.push(
                            i18n.t("contactImportWpModal.import.rowMissingFields", {
                                row: index + 1,
                                fields: missingInRow.join(", ")
                            }) || `Fila ${index + 1}: Faltan campos obligatorios (${missingInRow.join(", ")})`
                        );
                        continue;
                    }

                    if (contactData.number) {
                        contactData.number = contactData.number.toString().replace(/\D/g, '');
                    }

                    try {
                        const response = await api.post('/contactsImport', {
                            ...contactData,
                            validateContact: validateContact ? "true" : "false",
                        });

                        if (response.status === 200) {
                            importedCount++;
                        } else {
                            ignoredCount++;
                            errorsList.push(`Fila ${index + 1}: Error al procesar el contacto`);
                        }
                    } catch (error) {
                        ignoredCount++;
                        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Error desconocido";
                        errorsList.push(`Fila ${index + 1}: ${errorMsg}`);
                        toastError(`Fila ${index + 1}: ${errorMsg}`);
                        setUploading(false);
                        break;
                    }
                }
            }

            setCountCreated(importedCount);
            setCountIgnored(ignoredCount);
            setImportErrors(errorsList);
            setValidateContact(false);
            setSelectedRows({});
            setImported(true);
            setUploading(false);

            if (importedCount > 0) {
                if (ignoredCount === 0) {
                    toast.success(i18n.t("contactImportWpModal.import.successImportMsg", { count: importedCount }));
                } else {
                    toast.warn(i18n.t("contactImportWpModal.import.warnImportMsg", { count: importedCount, ignored: ignoredCount }));
                }
            } else {
                toast.error(i18n.t("contactImportWpModal.import.errorImportMsg"));
            }
        }
    };

    const onChangeFile = (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        setOpeningFile(true);
        setInvalidFile(false);
        setImported(false);
        setUploading(false);
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = function (e) {
            try {
                const data = e.target.result;
                const wb = read(data, { codepage: 65001 });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const {rows, columns} = WorksheetToDatagrid(ws);
                setRows(rows);
                setColumns(columns);

                // Auto-mapeo inteligente de cabeceras y selección de filas por defecto
                if (rows && rows.length > 0 && columns && columns.length > 0) {
                    const autoColumnValue = {};
                    const autoSelection = {};
                    const autoSelectedFields = {};

                    columns.forEach((col) => {
                        const rawTitle = col.headerTitle || "";
                        const norm = rawTitle
                            .trim()
                            .toLowerCase()
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "");

                        let fieldId = null;

                        if (["marca", "brand"].some(k => norm.includes(k))) fieldId = "marca";
                        else if (["conexion", "whatsapp", "connection", "conexao"].some(k => norm.includes(k))) fieldId = "conexion";
                        else if (["fila", "queue", "departamento", "setor", "cola"].some(k => norm.includes(k))) fieldId = "queue";
                        else if (["agente", "agent", "usuario", "user", "responsable", "atendente"].some(k => norm.includes(k))) fieldId = "agente";
                        else if (["nombre", "name", "nome", "contacto", "contato", "contact"].some(k => norm.includes(k))) fieldId = "name";
                        else if (["numero", "number", "telefono", "telefone", "celular", "phone"].some(k => norm.includes(k))) fieldId = "number";
                        else if (["tablero", "kanbanboard", "board", "embudo", "funil", "pipeline"].some(k => norm.includes(k))) fieldId = "kanbanBoard";
                        else if (["etapa", "kanbanlane", "lane", "fase"].some(k => norm.includes(k))) fieldId = "kanbanLane";
                        else if (["etiqueta", "etiquetas", "tags", "tag"].some(k => norm.includes(k))) fieldId = "tags";
                        else if (["correo", "email", "e-mail", "electronic"].some(k => norm.includes(k))) fieldId = "email";
                        else if (["cumpleanos", "birthdate", "nascimento", "nacimiento", "aniversario"].some(k => norm.includes(k))) fieldId = "birthDate";
                        else if (["cartera", "carteira", "wallet"].some(k => norm.includes(k))) fieldId = "carteira";

                        if (fieldId && !autoSelectedFields[fieldId]) {
                            autoColumnValue[col.key] = fieldId;
                            autoSelection[fieldId] = col.key;
                            autoSelectedFields[fieldId] = col.key;
                        }
                    });

                    setColumnValue(autoColumnValue);
                    setSelection(autoSelection);
                    setSelectedFields(autoSelectedFields);

                    const initialSelectedRows = {};
                    for (let r = 1; r < rows.length; r++) {
                        initialSelectedRows[r] = true;
                    }
                    setSelectedRows(initialSelectedRows);
                }

                setOpeningFile(false);
            } catch (e) {
                console.error(e);
                setInvalidFile(true);
                setOpeningFile(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSelectChange = (event) => {
        const newValue = event.target.value;
        const columnKey = event.target.name;

        // Si se seleccionó la opción vacía: desasociar la columna
        if (newValue === "") {
            const oldValue = columnValue[columnKey];
            setColumnValue((prev) => {
                const next = { ...prev };
                delete next[columnKey];
                return next;
            });
            if (oldValue) {
                setSelection((prev) => {
                    const next = { ...prev };
                    delete next[oldValue];
                    return next;
                });
                setSelectedFields((prev) => {
                    const next = { ...prev };
                    delete next[oldValue];
                    return next;
                });
            }
            return;
        }

        // Si ya está seleccionado exactamente ese valor en esta misma columna, no hacer nada
        if (columnValue[columnKey] === newValue) {
            return;
        }

        // Si este campo ya estaba seleccionado en otra columna, liberarlo de la otra columna
        const previousColumnKey = selection[newValue];
        if (previousColumnKey && previousColumnKey !== columnKey) {
            setColumnValue((prev) => {
                const next = { ...prev };
                delete next[previousColumnKey];
                next[columnKey] = newValue;
                return next;
            });
        } else {
            setColumnValue((prev) => ({ ...prev, [columnKey]: newValue }));
        }

        // Si esta columna tenía antes otro campo, liberarlo
        const previousFieldInThisCol = columnValue[columnKey];

        setSelection((prev) => {
            const next = { ...prev };
            if (previousFieldInThisCol) {
                delete next[previousFieldInThisCol];
            }
            next[newValue] = columnKey;
            return next;
        });

        setSelectedFields((prev) => {
            const next = { ...prev };
            if (previousFieldInThisCol) {
                delete next[previousFieldInThisCol];
            }
            next[newValue] = columnKey;
            return next;
        });
    };

    const renderSelectbox = (column) => {
        const currentFieldId = columnValue[column.key] || "";
        const currentField = contactFields.find(f => f.id === currentFieldId);
        const isRequired = currentField?.required;

        return (
            <Select
                value={currentFieldId}
                name={column.key}
                onChange={handleSelectChange}
                variant="outlined"
                margin="dense"
                style={{
                    fontSize: "0.85rem",
                    fontWeight: currentFieldId ? 600 : 400,
                    backgroundColor: currentFieldId 
                        ? (isRequired 
                            ? (theme.palette.type === 'dark' ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9') 
                            : (theme.palette.type === 'dark' ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd'))
                        : undefined,
                    borderRadius: 6,
                    width: "100%",
                }}
            >
                <MenuItem value="" style={{ fontStyle: "italic", color: "#999" }}>
                    <em>{i18n.t("contactImportWpModal.import.unmapped") || "(Sin asignar)"}</em>
                </MenuItem>
                {contactFieldsAvailable.map((contactField) => (
                    <MenuItem key={contactField.id} value={contactField.id}>
                        {contactField.label} {contactField.required ? " *" : ""}
                    </MenuItem>
                ))}
            </Select>
        );
    };

    const renderXls = () => {
        return (
            <TableContainer className={classes.tableContainer} style={{height: size.height * 0.65}}>
                <Table stickyHeader size="small">
                    <TableHead key={columns.length}>
                        <TableRow>
                            <TableCell style={{ width: 50, padding: "8px 12px" }}>
                                <input
                                    type="checkbox"
                                    checked={rows.length > 1 && Object.keys(selectedRows).length === rows.length - 1}
                                    onChange={(event) => {
                                        const isChecked = event.target.checked;
                                        const newSelectedRows = {};
                                        if (isChecked) {
                                            for (let i = 1; i < rows.length; i++) {
                                                newSelectedRows[i] = true;
                                            }
                                        }
                                        setSelectedRows(newSelectedRows);
                                    }}
                                />
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell key={column.key} style={{ minWidth: 170, padding: "8px 10px", verticalAlign: "middle" }}>
                                    {renderSelectbox(column)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex} hover>
                                {rowIndex !== 0 && (
                                    <TableCell style={{ width: 50, padding: "6px 12px" }}>
                                        <input
                                            type="checkbox"
                                            checked={!!selectedRows[rowIndex]}
                                            onChange={() => {
                                                setSelectedRows((prevSelectedRows) => ({
                                                    ...prevSelectedRows,
                                                    [rowIndex]: !prevSelectedRows[rowIndex],
                                                }));
                                            }}
                                        />
                                    </TableCell>
                                )}
                                {rowIndex !== 0 && (
                                    row.map((column, columnIndex) => {
                                        let cellValue = column;

                                        if (columnValue[columnIndex] === 'birthDate' && column) {
                                            try {
                                                if (typeof column === 'number' && column > 1 && column < 100000) {
                                                    const convertedDate = excelSerialToDate(column);
                                                    if (convertedDate && !isNaN(convertedDate.getTime())) {
                                                        const day = String(convertedDate.getDate()).padStart(2, '0');
                                                        const month = String(convertedDate.getMonth() + 1).padStart(2, '0');
                                                        const year = convertedDate.getFullYear();
                                                        cellValue = `${day}/${month}/${year}`;
                                                    }
                                                } else if (typeof column === 'string' && column.includes('/')) {
                                                    const parts = column.split('/');
                                                    if (parts.length === 3) {
                                                        let first = parseInt(parts[0]);
                                                        let second = parseInt(parts[1]);
                                                        let third = parts[2];
                                                        if (first > 12) {
                                                            cellValue = `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${third.padStart(4, '0')}`;
                                                        } else {
                                                            const year = third.length === 2 ? '19' + third : third.padStart(4, '0');
                                                            cellValue = `${String(second).padStart(2, '0')}/${String(first).padStart(2, '0')}/${year}`;
                                                        }
                                                    }
                                                }
                                            } catch (error) {
                                                console.warn('Erro ao formatar data para visualização:', error);
                                            }
                                        }

                                        // Validar celda (campos obligatorios vacíos o datos no existentes en BD)
                                        const validation = validateCell(row, columnIndex);

                                        if (!validation.valid) {
                                            return (
                                                <TableCell 
                                                    key={columnIndex} 
                                                    style={{ 
                                                        padding: "6px 12px",
                                                        backgroundColor: theme.palette.type === 'dark' ? 'rgba(244, 67, 54, 0.2)' : '#ffebee',
                                                        border: '1px solid #ef5350'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ 
                                                            fontSize: '0.85rem', 
                                                            color: theme.palette.type === 'dark' ? '#ffcdd2' : '#c62828',
                                                            textDecoration: cellValue ? 'line-through' : 'none'
                                                        }}>
                                                            {cellValue || <i style={{ opacity: 0.7 }}>(vacío)</i>}
                                                        </span>
                                                        <span style={{ 
                                                            color: '#d32f2f', 
                                                            fontSize: '0.75rem', 
                                                            fontWeight: 700, 
                                                            marginTop: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 3
                                                        }}>
                                                            ⚠️ {validation.message}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell key={columnIndex} style={{ padding: "6px 12px" }}>
                                                {cellValue}
                                            </TableCell>
                                        );
                                    })
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const handleCloseImport = async () => {
        try {
            history.push("/contacts");
        } catch (err) {
            toastError(err);
        }
    };

    const renderContent = () => {
        // 1. Columnas obligatorias que no están mapeadas
        const missingRequiredColumns = contactFields.filter(f => f.required && !selection[f.id]);

        // 2. Filas que tienen datos obligatorios vacíos o datos que no existen en el sistema
        let rowsWithMissingRequired = 0;
        const missingFieldsInData = new Set();

        let invalidEntityCount = 0;
        const invalidFieldsInData = new Set();

        if (rows && rows.length > 1 && columns && columns.length > 0) {
            for (let r = 1; r < rows.length; r++) {
                const rowItem = rows[r];
                let rowHasMissing = false;

                for (let c = 0; c < columns.length; c++) {
                    const validation = validateCell(rowItem, c);
                    if (!validation.valid) {
                        if (validation.errorType === "empty") {
                            rowHasMissing = true;
                            missingFieldsInData.add(validation.fieldName);
                        } else {
                            invalidEntityCount++;
                            invalidFieldsInData.add(validation.fieldName);
                        }
                    }
                }
                if (rowHasMissing) {
                    rowsWithMissingRequired++;
                }
            }
        }

        const hasBlockingErrors = missingRequiredColumns.length > 0 || rowsWithMissingRequired > 0 || invalidEntityCount > 0;

        return (
            <div>
                {/* Banner de estado de plantilla y validación de datos */}
                {invalidEntityCount > 0 ? (
                    <div style={{
                        margin: "8px 0",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        backgroundColor: theme.palette.type === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#fdecea',
                        border: '1px solid #d32f2f',
                        color: theme.palette.type === 'dark' ? '#ef9a9a' : '#b71c1c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>❌</span>
                        <span>
                            {i18n.t("contactImportWpModal.import.rowsWithInvalidEntities", {
                                count: invalidEntityCount,
                                fields: Array.from(invalidFieldsInData).join(", "),
                                interpolation: { escapeValue: false }
                            })}
                        </span>
                    </div>
                ) : missingRequiredColumns.length > 0 ? (
                    <div style={{
                        margin: "8px 0",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        backgroundColor: theme.palette.type === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff4e5',
                        border: '1px solid #ff9800',
                        color: theme.palette.type === 'dark' ? '#ffb74d' : '#663c00',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                        <span>
                            {i18n.t("contactImportWpModal.import.templateWarning", {
                                fields: missingRequiredColumns.map(f => f.label).join(", "),
                                interpolation: { escapeValue: false }
                            })}
                        </span>
                    </div>
                ) : rowsWithMissingRequired > 0 ? (
                    <div style={{
                        margin: "8px 0",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        backgroundColor: theme.palette.type === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff4e5',
                        border: '1px solid #ff9800',
                        color: theme.palette.type === 'dark' ? '#ffb74d' : '#663c00',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                        <span>
                            {i18n.t("contactImportWpModal.import.rowsWithMissingData", {
                                count: rowsWithMissingRequired,
                                fields: Array.from(missingFieldsInData).join(", "),
                                interpolation: { escapeValue: false }
                            })}
                        </span>
                    </div>
                ) : (
                    <div style={{
                        margin: "8px 0",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        backgroundColor: theme.palette.type === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#edf7ed',
                        border: '1px solid #4caf50',
                        color: theme.palette.type === 'dark' ? '#81c784' : '#1e4620',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>✅</span>
                        <span>{i18n.t("contactImportWpModal.import.templateSuccess")}</span>
                    </div>
                )}

                <div className={classes.importOptions} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    padding: '8px 16px',
                    gap: 12
                }}>
                    <FormControlLabel
                        control={
                            <Switch checked={validateContact} onChange={(event) => setValidateContact(event.target.checked)} color="primary"/>
                        }
                        label={i18n.t("contactImportWpModal.import.validateContacts")}
                        style={{ margin: 0 }}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {uploading && (
                            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, color: '#00a8ff', fontSize: '0.9rem' }}>
                                <CircularProgress size={16} color="inherit" />
                                {i18n.t("contactImportWpModal.import.importingWait")}
                            </div>
                        )}
                        <Button
                            variant="contained"
                            disabled={uploading || hasBlockingErrors}
                            onClick={() => processImport()}
                            className={classes.btnImport}
                            style={{ 
                                opacity: hasBlockingErrors ? 0.6 : 1
                            }}
                        >
                            {i18n.t("contactImportWpModal.buttons.importContacts")}
                        </Button>

                        <Button
                            variant="contained"
                            disabled={uploading}
                            onClick={() => {
                                setRows(null);
                                setColumns(null);
                                setColumnValue({});
                                setSelectedFields({});
                                setSelection({});
                            }}
                            className={classes.btnCancel}
                        >
                            {i18n.t("contactImportWpModal.buttons.cancel")}
                        </Button>
                    </div>
                </div>

                {error && <div className={classes.error} style={{ marginBottom: 8 }}>{error}</div>}

                {renderXls()}
            </div>
        );
    };

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: onChangeFile,
        maxFiles: 1,
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv'],
            'text/plain': ['.txt'],
        }
    });

    return (
        <div style={{alignContent: "center", padding: "10px 20px"}}>
            {imported && (
                <div style={{
                    padding: '24px',
                    margin: '24px auto',
                    maxWidth: '560px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }}>
                    <h3 style={{
                        textAlign: 'center',
                        margin: '0 0 18px 0',
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: theme.palette.text.primary
                    }}>
                        {i18n.t("contactImportWpModal.import.resultTitle")}
                    </h3>
                    <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                        <li style={{
                            padding: '12px 16px',
                            marginBottom: '10px',
                            backgroundColor: countCreated > 0 
                                ? (theme.palette.type === 'dark' ? 'rgba(46, 125, 50, 0.25)' : '#d4edda') 
                                : (theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa'),
                            borderRadius: '8px',
                            color: countCreated > 0 
                                ? (theme.palette.type === 'dark' ? '#81c784' : '#155724') 
                                : theme.palette.text.secondary,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{ fontSize: '1.2rem' }}>✅</span>
                            <span>{i18n.t("contactImportWpModal.import.successCreated", { count: countCreated })}</span>
                        </li>
                        <li style={{
                            padding: '12px 16px',
                            backgroundColor: countIgnored > 0 
                                ? (theme.palette.type === 'dark' ? 'rgba(237, 108, 2, 0.25)' : '#fff3cd') 
                                : (theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa'),
                            borderRadius: '8px',
                            color: countIgnored > 0 
                                ? (theme.palette.type === 'dark' ? '#ffb74d' : '#856404') 
                                : theme.palette.text.secondary,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                            <span>{i18n.t("contactImportWpModal.import.ignoredContacts", { count: countIgnored })}</span>
                        </li>
                    </ul>

                    {importErrors && importErrors.length > 0 && (
                        <div style={{
                            maxHeight: '180px',
                            overflowY: 'auto',
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: theme.palette.type === 'dark' ? 'rgba(211, 47, 47, 0.15)' : '#fff5f5',
                            borderRadius: '8px',
                            border: theme.palette.type === 'dark' ? '1px solid #d32f2f' : '1px solid #ffcdd2',
                            fontSize: '13px',
                            textAlign: 'left',
                            color: theme.palette.type === 'dark' ? '#ef9a9a' : '#c0392b'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>📋</span>
                                <span>{i18n.t("contactImportWpModal.import.detailsTitle") || "Detalle de errores:"}</span>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {importErrors.map((err, errIdx) => (
                                    <li key={errIdx} style={{ marginBottom: '4px' }}>{err}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div style={{display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px'}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setImported(false);
                                setRows(null);
                                setColumns(null);
                                setColumnValue({});
                                setSelectedFields({});
                                setSelection({});
                                setImportErrors([]);
                            }}
                        >
                            {i18n.t("contactImportWpModal.import.importMore")}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCloseImport}
                        >
                            {i18n.t("contactImportWpModal.buttons.closed")}
                        </Button>
                    </div>
                </div>
            )}
            {openingFile && (
                <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    margin: '20px auto',
                    maxWidth: '500px'
                }}>
                    <CircularProgress/>
                    <p>{i18n.t("contactImportWpModal.import.processing")}</p>
                </div>
            )}
            {invalidFile && (
                <div style={{
                    padding: '15px',
                    margin: '15px auto',
                    maxWidth: '500px',
                    border: '1px solid #f5c6cb',
                    borderRadius: '8px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    textAlign: 'center'
                }}>
                    <h3>{i18n.t("contactImportWpModal.import.invalidFileTitle")}</h3>
                    <p>{i18n.t("contactImportWpModal.import.invalidFileText")}</p>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setInvalidFile(false);
                            setRows(null);
                            setColumns(null);
                        }}
                        style={{marginTop: '10px'}}
                    >
                        {i18n.t("contactImportWpModal.import.tryAgain")}
                    </Button>
                </div>
            )}
            {!imported && !invalidFile && rows && columns ? renderContent() : (
                !imported && !invalidFile && !openingFile && (
                    <>
                        <Paper
                            elevation={0}
                            {...getRootProps()}
                            className={classes.dropzonePaper}
                        >
                            <input {...getInputProps()} />
                            <div className={classes.dropzoneIconBox}>
                                <CloudUploadIcon />
                            </div>
                            <Typography className={classes.dropzoneTitle}>
                                {i18n.t("contactImportWpModal.import.dragAndDrop")}
                            </Typography>
                            <Typography className={classes.dropzoneSubtitle}>
                                {i18n.t("contactImportWpModal.import.supportedSubtitle") || "Arrastra y suelta tu archivo o haz clic para explorar en tu equipo"}
                            </Typography>
                            <div className={classes.formatsWrapper}>
                                <Chip label=".XLSX" size="small" className={classes.formatChip} />
                                <Chip label=".XLS" size="small" className={classes.formatChip} />
                                <Chip label=".CSV" size="small" className={classes.formatChip} />
                                <Chip label=".TXT" size="small" className={classes.formatChip} />
                            </div>
                            <div className={classes.requirementsAlert}>
                                <InfoOutlinedIcon className={classes.requirementsIcon} />
                                <Typography className={classes.requirementsText}>
                                    {i18n.t("contactImportWpModal.import.minColumns")}
                                </Typography>
                            </div>
                        </Paper>

                        <div className={classes.backButtonContainer}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                className={classes.backButton}
                                disabled={uploading}
                                onClick={handleCloseImport}
                            >
                                {i18n.t("contactImportWpModal.buttons.back")}
                            </Button>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default ContactImport;
